/**
 * NarrativeMarkdown — render the SRE narrative produced by the
 * /investigate agent (and elsewhere) as a properly-formatted document.
 *
 * The investigation agent emits markdown: `## Headings`, `**bold**`,
 * inline `code`, fenced ```python``` blocks, and GFM tables. Rendering
 * the raw string inside <pre> shows the syntax verbatim (`## Heading`
 * instead of an actual heading, `**bold**` instead of bold text), so
 * we run it through react-markdown.
 *
 * Inline-vs-fenced code detection: react-markdown v9 removed the
 * `inline` prop on the <code> override. Fenced blocks now arrive
 * with a className like `language-python`; inline `code` arrives
 * with no className and is NOT wrapped in <pre>. We render <code>
 * inline-styled by default and let the <pre> override handle the
 * fenced-block container styling. The inner <code> inside a fenced
 * block keeps the `language-X` className so rehype-highlight's
 * hljs spans can color-style via the highlight.js theme.
 *
 * No rehype-raw — raw HTML is dropped. XSS-safe.
 */

import type { ReactNode } from 'react';
import {
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';

import 'highlight.js/styles/github.css';

// Inline `code` — small monospace pill, flows with surrounding text.
// Uses `display: inline` so it never breaks the paragraph.
// Dark-theme: translucent surface so cream text stays legible.
const inlineCodeSx = {
  display: 'inline',
  px: 0.5,
  py: 0,
  mx: 0.25,
  bgcolor: 'rgba(255, 255, 255, 0.04)',
  color: 'text.primary',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 0.5,
  fontFamily:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
  fontSize: '0.85em',
  whiteSpace: 'nowrap',
} as const;

// Fenced code block — block-level wrapper with subtle border.
// The inner <code class="language-X"> picks up highlight.js coloring
// from the imported github.css theme.
const preWrapperSx = {
  m: 0,
  my: 2,
  p: 0,
  bgcolor: 'background.elev2',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'auto',
  // Reset the inline-code spans inside this wrapper so the fenced block
  // doesn't render as a row of pills.
  '& code': {
    display: 'block',
    p: 1.5,
    bgcolor: 'transparent',
    border: 'none',
    borderRadius: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.55,
    whiteSpace: 'pre',
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
  },
  // highlight.js's github theme sets a transparent background by default
  // but some forks set white — make sure tokens are colored, not the box.
  '& code.hljs': {
    background: 'transparent',
  },
} as const;

export interface NarrativeMarkdownProps {
  text: string;
}

export function NarrativeMarkdown({ text }: NarrativeMarkdownProps) {
  return (
    <Box
      sx={{
        // Trim leading/trailing margins so the renderer sits flush in
        // its parent card.
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-of-type': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
        components={{
          // ---- Headings ----
          // The page already has a "Narrative" h6 wrapper above this
          // component, so markdown headings step DOWN from there:
          //   markdown h2 → subtitle1 (16px / weight 600)
          //   markdown h3 → subtitle2 (14px / weight 600)
          //   markdown h4–h6 → body2 weight 600
          // Spacing follows the 8dp rhythm: top margin sets section
          // separation, bottom margin is tight to the heading's body.
          h1: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="subtitle1"
              component="h2"
              sx={{ mt: 3, mb: 1, fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h2: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{ mt: 3, mb: 1, fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="subtitle2"
              component="h4"
              sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h4: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="body2"
              component="h5"
              sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h5: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="body2"
              component="h6"
              sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h6: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="body2"
              component="h6"
              sx={{ mt: 1.5, mb: 0.5, fontWeight: 600, color: 'text.secondary' }}
            >
              {children}
            </Typography>
          ),

          // ---- Paragraph + inline ----
          p: ({ children }: { children?: ReactNode }) => (
            <Typography
              variant="body2"
              component="p"
              sx={{ my: 1, lineHeight: 1.65 }}
            >
              {children}
            </Typography>
          ),
          strong: ({ children }: { children?: ReactNode }) => (
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {children}
            </Box>
          ),
          em: ({ children }: { children?: ReactNode }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>
              {children}
            </Box>
          ),
          a: ({ children, href }: { children?: ReactNode; href?: string }) => (
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {children}
            </Link>
          ),
          hr: () => <Divider sx={{ my: 2 }} />,

          // ---- Lists ----
          // Tighter than default (engineering tool — density bias).
          ul: ({ children }: { children?: ReactNode }) => (
            <Box component="ul" sx={{ my: 1, pl: 3, listStyleType: 'disc' }}>
              {children}
            </Box>
          ),
          ol: ({ children }: { children?: ReactNode }) => (
            <Box component="ol" sx={{ my: 1, pl: 3, listStyleType: 'decimal' }}>
              {children}
            </Box>
          ),
          li: ({ children }: { children?: ReactNode }) => (
            <Box
              component="li"
              sx={{
                my: 0.25,
                fontSize: 'body2.fontSize',
                lineHeight: 1.65,
                // The default browser list styling sets ::marker color
                // separately; align it with body color rather than blue.
                '&::marker': { color: 'text.secondary' },
              }}
            >
              {children}
            </Box>
          ),

          // ---- Block quote ----
          blockquote: ({ children }: { children?: ReactNode }) => (
            <Box
              component="blockquote"
              sx={{
                my: 2,
                pl: 2,
                borderLeft: '3px solid',
                borderColor: 'primary.light',
                color: 'text.secondary',
                bgcolor: 'background.elev2',
                py: 0.5,
              }}
            >
              {children}
            </Box>
          ),

          // ---- Code ----
          // Inline `code` arrives with no className. Fenced blocks
          // arrive with `language-<lang>` and are wrapped in <pre>.
          // We style the inline path here; the <pre> override handles
          // the block container, and the inner <code className="language-X">
          // gets reset by the wrapper's `& code` rule.
          code: ({ className, children }: { className?: string; children?: ReactNode }) => {
            const isFenced = className?.startsWith('language-') ?? false;
            if (isFenced) {
              // Hand off to the <pre> wrapper. Keep the className so
              // rehype-highlight's hljs spans can color via github.css.
              return <code className={className}>{children}</code>;
            }
            return (
              <Box component="code" sx={inlineCodeSx}>
                {children}
              </Box>
            );
          },
          pre: ({ children }: { children?: ReactNode }) => (
            <Box component="pre" sx={preWrapperSx}>
              {children}
            </Box>
          ),

          // ---- Tables (GFM) ----
          table: ({ children }: { children?: ReactNode }) => (
            <TableContainer
              sx={{
                my: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Table size="small">{children}</Table>
            </TableContainer>
          ),
          thead: ({ children }: { children?: ReactNode }) => (
            <TableHead sx={{ bgcolor: 'background.elev2' }}>{children}</TableHead>
          ),
          tbody: ({ children }: { children?: ReactNode }) => (
            <TableBody>{children}</TableBody>
          ),
          tr: ({ children }: { children?: ReactNode }) => <TableRow>{children}</TableRow>,
          th: ({ children }: { children?: ReactNode }) => (
            <TableCell>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {children}
              </Typography>
            </TableCell>
          ),
          td: ({ children }: { children?: ReactNode }) => (
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                {children}
              </Typography>
            </TableCell>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </Box>
  );
}
